import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useToast } from '../context/ToastContext';
import { trackApplyClick } from '../lib/analytics';
import { ArrowLeft, Loader2 } from 'lucide-react';
import Logo from './Logo';

const LoginPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetMessage, setResetMessage] = useState<string | null>(null);

  // Check valid invite
  const canSignUp = searchParams.get('signup') === 'true';

  // Force signup mode if parameter is present on load
  React.useEffect(() => {
    if (canSignUp) {
      setIsSignUp(true);
    }
  }, [canSignUp]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (isSignUp) {
        // Verify email belongs to an approved applicant before allowing signup
        const { data: application, error: appError } = await supabase
          .from('applications')
          .select('id, status')
          .eq('email', email.toLowerCase().trim())
          .eq('status', 'approved')
          .maybeSingle();

        if (appError) {
          throw new Error('Unable to verify application status. Please try again.');
        }

        if (!application) {
          throw new Error('No approved application found for this email. You must be approved before creating an account.');
        }

        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: window.location.origin + '/dashboard',
          },
        });
        if (error) throw error;
        showToast('Check your email for the confirmation link!', 'info');
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
        // Navigation will be handled by auth state change in AuthContext
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address first.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setResetMessage(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + '/reset-password',
      });

      if (error) throw error;
      setResetMessage('Check your email for the password reset link!');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md animate-fade-in">
      {/* Back Button */}
      <Link
        to="/"
        className="mb-8 flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-purple-600 transition-colors group"
      >
        <ArrowLeft className="w-4 h-4 transform group-hover:-translate-x-1 transition-transform" />
        Back to Home
      </Link>

      <div className="bg-white p-8 lg:p-10 rounded-[2.5rem] shadow-2xl border border-gray-100">
        <div className="text-center mb-10">
          <div className="flex justify-center mb-6">
            <Logo size="lg" showText={false} />
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-2">
            {isForgotPassword ? 'Reset Password' : isSignUp ? 'Create Account' : 'Creator Login'}
          </h2>
          <p className="text-gray-500">
            {isForgotPassword
              ? 'Enter your email to receive a reset link.'
              : isSignUp
                ? 'Set up your account to start earning.'
                : 'Welcome back! Manage your submissions and earnings.'}
          </p>
        </div>

        {resetMessage && (
          <div className="mb-6 p-4 bg-purple-50 text-purple-600 rounded-xl text-sm font-bold text-center">
            {resetMessage}
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-xl text-sm font-bold text-center">
            {error}
          </div>
        )}

        <form onSubmit={isForgotPassword ? handleForgotPassword : handleAuth} className="space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-bold text-gray-700">Email Address</label>
            <input
              required
              type="email"
              placeholder="name@example.com"
              className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-violet-400 focus:border-violet-400 outline-none transition-all"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          {!isForgotPassword && (
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="block text-sm font-bold text-gray-700">Password</label>
                {!isSignUp && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsForgotPassword(true);
                      setError(null);
                      setResetMessage(null);
                    }}
                    className="text-xs font-bold text-purple-600 hover:underline"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <input
                required
                type="password"
                placeholder="••••••••"
                className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-violet-400 focus:border-violet-400 outline-none transition-all"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-5 bg-black text-white rounded-full text-lg font-bold hover:bg-purple-800 transition-all shadow-lg hover:shadow-purple-500/20 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (isForgotPassword ? 'Send Reset Link' : isSignUp ? 'Create Account' : 'Sign In')}
          </button>

          {isForgotPassword && (
            <div className="text-center pt-4">
              <button
                type="button"
                onClick={() => {
                  setIsForgotPassword(false);
                  setError(null);
                  setResetMessage(null);
                }}
                className="text-gray-500 text-sm hover:text-purple-600 transition-colors"
              >
                ← Back to Login
              </button>
            </div>
          )}
        </form>

        <div className="mt-10 pt-8 border-t border-gray-100 text-center">
          {canSignUp ? (
            <p className="text-gray-500 mb-4">
              {isSignUp ? 'Already have an account?' : 'Received an invite?'}
              <button
                onClick={() => setIsSignUp(!isSignUp)}
                className="ml-2 text-purple-600 font-bold hover:underline"
              >
                {isSignUp ? 'Sign In' : 'Create Account'}
              </button>
            </p>
          ) : null}

          {!isSignUp && !isForgotPassword && (
            <p className="text-gray-500">
              Not a creator yet?{' '}
              <Link
                to="/apply"
                onClick={trackApplyClick}
                className="text-purple-600 font-bold hover:underline"
              >
                Apply now
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
